import {
  lazy,
  createElement,
  useEffect,
  type ComponentProps,
  type ComponentType,
  type LazyExoticComponent,
} from 'react';

import {
  clearChunkReloadFlag,
  isChunkLoadError,
  reloadAppOnce,
} from './reload';

type ComponentModule<T extends ComponentType> = { default: T };

type LazyPageOptions = {
  /** When true (default), clears the one-reload guard once this chunk mounts. */
  clearReloadFlagOnMount?: boolean;
};

const DYNAMIC_IMPORT_DEFAULT_EXPORT_ERROR_PATTERN =
  /Cannot read propert(?:y|ies) of (?:undefined|null) \(reading 'default'\)/i;

function isDynamicImportDefaultExportError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message : String(error ?? '');

  return DYNAMIC_IMPORT_DEFAULT_EXPORT_ERROR_PATTERN.test(message);
}

function withChunkReloadRecovery<T extends ComponentType>(Component: T): T {
  const RecoveredComponent = (props: ComponentProps<T>) => {
    useEffect(() => {
      clearChunkReloadFlag();
    }, []);

    return createElement(Component, props);
  };

  const componentName = Component.displayName || Component.name || 'Component';
  RecoveredComponent.displayName = `withChunkReloadRecovery(${componentName})`;

  return RecoveredComponent as T;
}

function importWithChunkRecovery<T extends ComponentType>(
  componentImport: () => Promise<ComponentModule<T>>,
): Promise<ComponentModule<T>> {
  return new Promise((resolve, reject) => {
    componentImport()
      .then(resolve)
      .catch((error: unknown) => {
        const isChunkError =
          isChunkLoadError(error) ||
          isDynamicImportDefaultExportError(error);

        if (isChunkError && reloadAppOnce()) {
          return;
        }

        reject(error);
      });
  });
}

export default function lazyPage<T extends ComponentType>(
  componentImport: () => Promise<ComponentModule<T>>,
  chunkIdentifier: string,
  { clearReloadFlagOnMount = true }: LazyPageOptions = {},
): LazyExoticComponent<T> {
  return lazy(() =>
    importWithChunkRecovery(componentImport).then((module) => {
      if (!module.default) {
        throw new Error(
          `Lazy page "${chunkIdentifier}" must have a default export.`,
        );
      }

      return {
        default: clearReloadFlagOnMount
          ? withChunkReloadRecovery(module.default)
          : module.default,
      };
    }),
  );
}
